import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import { Icon, Table, Segment, Button, Popup, Checkbox, Label, Input } from 'semantic-ui-react';
import EventsTable from './Table';
import { filter, some } from 'lodash';

import { getEvents } from '../actions/eventActions';
import { getFuneralHomes } from '../actions/funeralHomeActions';

import './../packs/index.scss';

const convertDateToDateOnly = (unformattedDate) => {
  if (unformattedDate) {
    return moment(unformattedDate).format('MMMM Do YYYY');
  }

  return '';
} 

const convertDateToTimeOnly = (unformattedDate) => {
  if (unformattedDate) {
    return moment(unformattedDate).format('h:mm a');
  }

  return '';
}

const renderClientEmails = (emails) => {
  return emails.map((email, index) => {
    return <div key={index}>{email}</div>
  });
}

const renderSubRow = ({row, rowProps}) => {
  let rowsToRender = [
    (<Table.Row className="innerTableHeader" key="title">
      <Table.Cell className="indendentedRow">Stream Name</Table.Cell>
      <Table.Cell className="indendentedRow">Stream Date</Table.Cell>
      <Table.Cell className="indendentedRow">Start Time</Table.Cell>
      <Table.Cell className="indendentedRow">End Time</Table.Cell>
      <Table.Cell className="indendentedRow">Site</Table.Cell>
      <Table.Cell className="indendentedRow">Location</Table.Cell>
    </Table.Row>)
  ];

  row.original.streams.forEach((stream, index) => {
    rowsToRender.push(       
      <Table.Row key={index}>
        <Table.Cell className="indendentedRow">{stream.name}</Table.Cell>
        <Table.Cell className="indendentedRow">{convertDateToDateOnly(stream.streamStart)}</Table.Cell>
        <Table.Cell className="indendentedRow">{convertDateToTimeOnly(stream.streamStart)}</Table.Cell>
        <Table.Cell className="indendentedRow">{convertDateToTimeOnly(stream.streamEnd)}</Table.Cell>
        <Table.Cell className="indendentedRow">{stream.location}</Table.Cell>
        <Table.Cell className="indendentedRow">{stream.room}</Table.Cell>
      </Table.Row>
    );
  });

  return (
    <React.Fragment>
      {rowsToRender}
    </React.Fragment>
  );
}

const EventsPage = ({ events, getEvents, funeralHomes, getFuneralHomes, user }) => {
  const [selectedFuneralHomes, setSelectedFuneralHomes] = useState(["All"]);
  const [selectedDate, setSelectedDate] = useState([null]);
  
  useEffect(() => {
    getEvents();
    getFuneralHomes();
  }, []);

  const renderStatusPills = (value) => {
    if (user.user && user.user.superadminRole) {
      return (
        <React.Fragment>
          <div>
            {value && value.googleCalendarCreated && <Label className="successLabel"><Icon color='green' name='checkmark' size='small' /> Calendar</Label>}
            {value && !value.googleCalendarCreated && <Label className="failLabel"><Icon color='red' name='close' size='small' /> Calendar</Label>}
          </div>
          <div>
            {value && value.livestreamCreated && <Label className="successLabel"><Icon color='green' name='checkmark' size='small' /> Livestream API</Label>}
            {value && !value.livestreamCreated && <Label className="failLabel"><Icon color='red' name='close' size='small' /> Livestream API</Label>}
          </div>
        </React.Fragment>
      )
    }

    return (
      <div>
        {value && value.livestreamCreated && value.googleCalendarCreated ? 
          <Label className="successLabel"><Icon color='green' name='checkmark' size='small' />  Accepted</Label> :
          <Label className="warningLabel"><Icon color='grey' name='clock outline' size='small' /> In Progress</Label>
        }
      </div>
    )
  }

  const updateSelectedFuneralHomes = (selectedOption, checked) => {
    if (checked) {
      const homes = [...selectedFuneralHomes, selectedOption];
      setSelectedFuneralHomes(homes);
    } else {
      const homes = [ ...selectedFuneralHomes ];
      const index = homes.indexOf(selectedOption);
      if (index > -1) {
        homes.splice(index, 1);
        setSelectedFuneralHomes(homes);
      }
    }
  }

  const renderFuneralHomes = (funeralHomes) => {
    return funeralHomes.map((funeralHome, index) => {
      return <div key={index}><Checkbox disabled={selectedFuneralHomes.includes('All')} checked={selectedFuneralHomes.includes(funeralHome.id)} onChange={(e, data) => updateSelectedFuneralHomes(funeralHome.id, data.checked)} label={funeralHome.name} /></div>
    });
  }

  const columns = [
      {
        Header: 'Funeral Home Id',
        accessor: 'funeralHomeId',
        disableSortBy: true,
        disableGlobalFilter: true,
        filter: (rows, id, filterValue) => {
          return rows.filter((row) => {
            if (filterValue.length === 0 ) return false;
            if (filterValue.includes("All")) {
              return true;
            }
            const funeralHomeId = row.original.funeralHomeId;
            return filterValue.includes(funeralHomeId);
          });
        },
      },
      {
        Header: 'Deceased Name',
        accessor: (event) => `${event.lastName}, ${event.firstName}`,
        sortType: (rowA, rowB) => {
          if (rowA.original.firstName.toLocaleLowerCase() < rowB.original.firstName.toLocaleLowerCase())
            return -1;
          if ( rowA.original.firstName.toLocaleLowerCase() > rowB.original.firstName.toLocaleLowerCase())
            return 1;
          return 0;
        }
      },
      {
        Header: 'Status',
        accessor: 'eventStatus',
        Cell: ({ value }) =>  renderStatusPills(value),
        disableSortBy: true,
        disableGlobalFilter: true,
      },
      {
        Header: 'Client Email',
        accessor: 'clientEmails',
        Cell: ({ value }) =>  (<div>{value && value.emails && renderClientEmails(value.emails)}</div>),
        disableSortBy: true,
      },
      {
        Header: 'URL',
        accessor: 'desiredUrl',
        disableSortBy: true
      },
      {
        Header: 'Streams',
        accessor: 'streams',
        disableSortBy: true,
        disableGlobalFilter: true,
        Cell: ({ value }) =>  value ? value.length : 0,
        disableSortBy: false,
        filter: (rows, id, filterValue) => {
          return rows.filter((row) => {
            if (!filterValue) {
              return true;
            }
            const streams = row.original.streams;
            if (!streams || streams.length < 1) {
              return false;
            }

            return some(streams, (stream) => {
              return moment(filterValue).isSame(stream.streamStart, 'date');
            });
          });
        },
        sortType: (rowA, rowB) => {
          if (rowA.original.streams.length > rowB.original.streams.length) {
            return 1;
          }
          return -1;
        }
      },
      {
        // Build our expander column
        id: 'expander', // Make sure it has an ID
        Header: ({ getToggleAllRowsExpandedProps, isAllRowsExpanded }) => (
          <span>
            <Icon name="chevron circle down" size="large" />
          </span>
        ),
        disableGlobalFilter: true,
        Cell: ({ row }) =>
          // Use the row.canExpand and row.getToggleRowExpandedProps prop getter
          // to build the toggle for expanding a row
          // row.canExpand ? (
            <span
              {...row.getToggleRowExpandedProps({
                style: {
                  // We can even use the row.depth property
                  // and paddingLeft to indicate the depth
                  // of the row
                  paddingLeft: `${row.depth * 2}rem`,
                },
              })}
            >
              {row.isExpanded ? <Icon name="chevron circle up" size="large" /> : <Icon name="chevron circle down" size="large" />}
            </span>
          // ) : null,
      },
    ];

  return (
    <div style={{ padding: 50 }}>
      <h3 className="pageHeader">All Events</h3>
      <Segment>
        <Popup
          size="huge"
          position="bottom right"
          trigger={<Button className="filterDropDownButton" icon='filter' content='Filter' />}
          content={
            <div>
              {funeralHomes && user.user && user.user.superadminRole &&
                <div style={{ paddingBottom: 30 }}>
                  <h4>Funeral Homes</h4>
                  <div><Checkbox checked={selectedFuneralHomes.includes("All")} onChange={(e, data) => updateSelectedFuneralHomes("All", data.checked)} label="Select All" /></div>
                  {renderFuneralHomes(funeralHomes)}
                </div>
              }
              <div>
                <h4>Date</h4>
                <Input type="date" size="small" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
              </div>
            </div>
          }
          on="click"
        />
        {
          events && 
            <EventsTable 
              data={events} 
              columns={columns} 
              renderRowSubComponent={({row, rowProps}) => renderSubRow({row, rowProps})}
              dateFilterValue={selectedDate}
              selectedFuneralHomes={selectedFuneralHomes}
            />
        }
      </Segment>
    </div>
  )
};

const mapStateToProps = state => ({
  events: state.events,
  funeralHomes: state.funeralHomes,
  user: state.user,
});

const mapDispatchToProps = {
  getEvents,
  getFuneralHomes
};

export default connect(mapStateToProps, mapDispatchToProps)(EventsPage);